import { create } from 'zustand'

import { evaluarPrecios, type LineaAEvaluar, type Promocion } from '@/lib/precios'
import { momentoDelLocal } from '@/lib/reloj'
import { cantidadPorImporte, importeLinea } from '@/lib/calculoVenta'
import { Ulid } from '@/lib/ulid'
import type { LineaTicket, MedioPagoPos, PagoTicket, ProductoPos } from './tipos'

// Estado del ticket que se está armando.
//
// Vive en el navegador y no en el servidor a propósito: mientras el cajero
// carga líneas no hay nada que persistir, y un ticket a medias en la base es
// una fila que alguien va a tener que limpiar. Recién al cobrar se manda todo
// junto, con su ULID, y ahí sí existe.
//
// El total que muestra esta pantalla lo calcula el MISMO motor que usa el
// servidor (lib/precios.ts, gemelo de MotorPrecios.cs). No es una estimación
// que después se corrige: el corpus compartido garantiza que los dos dan el
// mismo número. Igual, el que se cobra es el del servidor.

let contador = 0
const nuevoUid = () => `l${++contador}`

interface EstadoTicket {
  lineas: LineaTicket[]
  pagos: PagoTicket[]

  /** Descuento sobre el total, en centavos. Requiere permiso. */
  descuentoCentavos: number

  /** Efectivo que entregó el cliente, para el vuelto. */
  entregadoCentavos: number | null

  /**
   * Cliente de cuenta corriente, cuando la venta se lleva fiada.
   *
   * Vive en el ticket y no dentro del diálogo de cobro porque sobrevive a
   * cerrar y volver a abrir el cobro: elegir de nuevo al cliente después de
   * corregir un importe es trabajo de más con gente esperando.
   */
  cliente: { id: number; nombre: string } | null

  /** Promociones vigentes, para la previsualización. Las carga la pantalla. */
  promociones: Promocion[]

  /** Línea seleccionada, para editarla con el teclado. */
  seleccionada: string | null

  // ── Acciones ──
  fijarPromociones: (p: Promocion[]) => void

  agregar: (
    producto: ProductoPos,
    opciones?: {
      cantidadMilesimas?: number
      importeObjetivoCentavos?: number
      codigoBalanzaLeido?: string
    },
  ) => void

  cambiarCantidad: (uid: string, cantidadMilesimas: number) => void
  sumarUnidad: (uid: string, signo: 1 | -1) => void
  descontarLinea: (uid: string, centavos: number) => void
  quitar: (uid: string) => void
  seleccionar: (uid: string | null) => void

  agregarPago: (medio: MedioPagoPos, centavos: number, referencia?: string) => void
  quitarPago: (indice: number) => void
  fijarEntregado: (centavos: number | null) => void
  fijarDescuento: (centavos: number) => void
  fijarCliente: (cliente: { id: number; nombre: string } | null) => void

  limpiar: () => void

  /**
   * Clave de idempotencia de ESTE ticket.
   *
   * Vive en el ticket y no se genera al mandar, y esa es la diferencia entre
   * que sirva o no: si se generara al mandar, el cajero que ve «no se pudo
   * registrar» y vuelve a apretar Cobrar mandaría un id nuevo, y si la primera
   * en realidad se guardó quedan DOS ventas con la misma mercadería y el doble
   * de plata en el arqueo. Con el id pegado al ticket, reintentar devuelve la
   * misma venta.
   *
   * Se renueva al limpiar, que es lo que pasa después de cobrar: el ticket
   * siguiente es otra venta.
   */
  ulid: string
}

export const useTicket = create<EstadoTicket>((set, get) => ({
  ulid: Ulid.nuevo(),
  lineas: [],
  pagos: [],
  descuentoCentavos: 0,
  entregadoCentavos: null,
  cliente: null,
  promociones: [],
  seleccionada: null,

  fijarPromociones: (promociones) => {
    set({ promociones })
    set({ lineas: recalcular(get().lineas, promociones) })
  },

  agregar: (producto, opciones) => {
    const { lineas, promociones } = get()

    // La venta por importe llega en PLATA («dame $3.000 de banana», y también
    // la etiqueta de la balanza en modo importe, que es el camino principal de
    // una etiquetadora) y hay que resolverla a cantidad ACÁ.
    //
    // Sin esto la línea se quedaba con la cantidad por defecto —un kilo— así
    // que el renglón decía «1,000 kg × $2.890» y el TOTAL de la pantalla
    // $2.890, mientras el servidor, que sí resuelve el importe, iba a cobrar
    // 1,038 kg y $2.999,82: la pantalla mostraba un número y la caja cobraba
    // otro. Se resuelve con `cantidadPorImporte` de lib/calculoVenta, que es
    // el gemelo verificado del que usa el servidor, así que los dos dan la
    // misma cantidad y el renglón no se desmiente al cobrar.
    //
    // Cantidad por defecto: una unidad. En algo pesable no se asume un kilo,
    // se asume que la pantalla va a pedir el peso; acá llega ya resuelto.
    const cantidad =
      opciones?.importeObjetivoCentavos !== undefined
        ? cantidadPorImporte(
            opciones.importeObjetivoCentavos,
            producto.precioCentavos,
            producto.decimalesCantidad,
          )
        : (opciones?.cantidadMilesimas ?? 1000)

    // Si el mismo producto ya está en el ticket y se agrega sin peso ni
    // importe, se suma a la línea que hay en lugar de abrir otra. Es lo que
    // espera el cajero cuando toca dos veces la misma lechuga, y deja el
    // ticket legible.
    const acumulable =
      !producto.pesable &&
      opciones?.importeObjetivoCentavos === undefined &&
      opciones?.codigoBalanzaLeido === undefined

    if (acumulable) {
      const existente = lineas.find((l) => l.producto.id === producto.id && !l.codigoBalanzaLeido)

      if (existente) {
        set({
          lineas: recalcular(
            lineas.map((l) =>
              l.uid === existente.uid
                ? { ...l, cantidadMilesimas: l.cantidadMilesimas + cantidad }
                : l,
            ),
            promociones,
          ),
          seleccionada: existente.uid,
        })

        return
      }
    }

    const uid = nuevoUid()

    const linea: LineaTicket = {
      uid,
      producto,
      cantidadMilesimas: cantidad,
      importeObjetivoCentavos: opciones?.importeObjetivoCentavos,
      descuentoCentavos: 0,
      codigoBalanzaLeido: opciones?.codigoBalanzaLeido,
      brutoCentavos: 0,
      promocionDescuentoCentavos: 0,
    }

    set({ lineas: recalcular([...lineas, linea], promociones), seleccionada: uid })
  },

  cambiarCantidad: (uid, cantidadMilesimas) => {
    if (cantidadMilesimas <= 0) return get().quitar(uid)

    set({
      lineas: recalcular(
        get().lineas.map((l) =>
          l.uid === uid
            ? { ...l, cantidadMilesimas, importeObjetivoCentavos: undefined }
            : l,
        ),
        get().promociones,
      ),
    })
  },

  sumarUnidad: (uid, signo) => {
    const linea = get().lineas.find((l) => l.uid === uid)
    if (!linea) return

    // El paso es la unidad mínima de la unidad de venta: en algo por unidad
    // suma de a 1, y en kilo de a 100 g (un cuarto no, porque el cajero
    // ajusta con el peso de la balanza, no con este botón).
    const paso = linea.producto.decimalesCantidad === 0 ? 1000 : 100

    get().cambiarCantidad(uid, linea.cantidadMilesimas + signo * paso)
  },

  descontarLinea: (uid, centavos) => {
    set({
      lineas: recalcular(
        get().lineas.map((l) =>
          l.uid === uid ? { ...l, descuentoCentavos: Math.max(0, centavos) } : l,
        ),
        get().promociones,
      ),
    })
  },

  quitar: (uid) => {
    const lineas = get().lineas.filter((l) => l.uid !== uid)

    set({
      lineas: recalcular(lineas, get().promociones),
      seleccionada: get().seleccionada === uid ? (lineas.at(-1)?.uid ?? null) : get().seleccionada,
    })
  },

  seleccionar: (uid) => set({ seleccionada: uid }),

  agregarPago: (medio, importeCentavos, referencia) =>
    set({ pagos: [...get().pagos, { medio, importeCentavos, referencia }] }),

  quitarPago: (indice) => set({ pagos: get().pagos.filter((_, i) => i !== indice) }),

  fijarEntregado: (entregadoCentavos) => set({ entregadoCentavos }),
  fijarDescuento: (descuentoCentavos) => set({ descuentoCentavos: Math.max(0, descuentoCentavos) }),
  fijarCliente: (cliente) => set({ cliente }),

  limpiar: () =>
    set({
      // Ticket nuevo, clave de idempotencia nueva.
      ulid: Ulid.nuevo(),

      lineas: [],
      pagos: [],
      descuentoCentavos: 0,
      entregadoCentavos: null,

      // El cliente también se limpia: la próxima venta es de otra persona, y
      // dejarlo pegado es la forma de anotarle a Doña Rosa la compra del
      // siguiente.
      cliente: null,

      seleccionada: null,
    }),
}))

// ── Cálculo ─────────────────────────────────────────────────────────────────

/**
 * Recalcula brutos y promociones de todas las líneas.
 *
 * Se recalcula TODO y no solo la línea que cambió, y es a propósito: hay
 * promociones que dependen de la cantidad total (un 3x2 se activa al llegar a
 * la tercera unidad), así que un cálculo incremental daría mal justo en el
 * caso que importa.
 */
function recalcular(lineas: LineaTicket[], promociones: Promocion[]): LineaTicket[] {
  const conBruto = lineas.map((l, i) => ({
    ...l,
    brutoCentavos: importeLinea(l.producto.precioCentavos, l.cantidadMilesimas),
    _orden: i + 1,
  }))

  const aEvaluar: LineaAEvaluar[] = conBruto.map((l) => ({
    orden: l._orden,
    productoId: l.producto.id,
    categoriaId: l.producto.categoriaId,
    cantidadMilesimas: l.cantidadMilesimas,
    precioUnitarioCentavos: l.producto.precioCentavos,
    decimalesCantidad: l.producto.decimalesCantidad,
    brutoCentavos: l.brutoCentavos,
  }))

  // El momento va en hora local del navegador: la vigencia de una promoción
  // («los miércoles», «de 18 a 20») se define en la hora del mostrador.
  // La hora del LOCAL y no la del navegador: la vigencia de una promoción se
  // define en la hora del mostrador («los miércoles de 18 a 20») y el servidor
  // la evalúa con la zona del local. Con `new Date()`, una PC en UTC activaba
  // la promo de la tarde a las tres y la pantalla mostraba un precio que la
  // caja no iba a cobrar. Ver lib/reloj.
  const resultado = evaluarPrecios(aEvaluar, promociones, momentoDelLocal())

  return conBruto.map(({ _orden, ...l }) => {
    const evaluada = resultado.lineas.find((e) => e.orden === _orden)

    return {
      ...l,
      promocionDescuentoCentavos: evaluada?.descuentoCentavos ?? 0,
      promocionNombre: evaluada?.detalle ?? evaluada?.promocionNombre ?? null,
    }
  })
}

/**
 * Importe de una línea: precio por cantidad, redondeado al centavo.
 *
 * Se re-exporta desde `lib/calculoVenta`, que es el gemelo verificado de
 * `CalculoVenta.cs`: la cuenta estaba escrita acá a mano y la equivalencia con
 * el `Math.Round(AwayFromZero)` de C# no la chequeaba nadie. Sigue exportada
 * con este nombre porque las pantallas la importan de acá.
 */
export { importeLinea }

// ── Selectores ──────────────────────────────────────────────────────────────

export function totalesDelTicket(estado: EstadoTicket) {
  const bruto = estado.lineas.reduce((a, l) => a + l.brutoCentavos, 0)

  const descuentoLineas = estado.lineas.reduce(
    (a, l) => a + Math.min(l.promocionDescuentoCentavos + l.descuentoCentavos, l.brutoCentavos),
    0,
  )

  const subtotal = bruto - descuentoLineas
  const descuento = Math.min(estado.descuentoCentavos, subtotal)
  const total = subtotal - descuento

  const cobrado = estado.pagos.reduce((a, p) => a + p.importeCentavos, 0)

  return {
    bruto,
    descuentoLineas,
    subtotal,
    descuento,
    total,
    cobrado,
    falta: Math.max(0, total - cobrado),
    vuelto:
      estado.entregadoCentavos !== null && estado.entregadoCentavos >= total
        ? estado.entregadoCentavos - total
        : null,
  }
}

/** Total de la línea después de promociones y descuento manual. */
export function totalLinea(l: LineaTicket): number {
  return Math.max(
    0,
    l.brutoCentavos - Math.min(l.promocionDescuentoCentavos + l.descuentoCentavos, l.brutoCentavos),
  )
}
